/**
 * Public voucher types — consumed by the /redeem landing page (Phase 01).
 *
 * Mirrors the backend payloads:
 *   - PublicVoucherSummaryResource  → PublicVoucherSummary
 *   - PublicVoucherGroupResource    → PublicVoucherGroup
 *   - OrderResource (verify return) → VoucherOriginalOrder (alias of Order)
 *
 * Source of truth: api/app/Http/Resources/Public/PublicVoucher*Resource.php
 */

import type { Order } from './index'

/**
 * Status of a single voucher as exposed by the public API.
 *
 * Order of precedence in the resource (must match backend):
 *   redeemed (terminal) → expired (terminal) → underlying VoucherStatus value.
 *
 * `unknown` is the defensive fallback for malformed/legacy rows.
 */
/**
 * Named voucher status values — the single source of truth for status comparisons
 * across components (W-09 / no magic strings). Use these constants instead of
 * inline literals like `voucher.status === 'redeemed'`.
 */
export const VOUCHER_STATUS_ACTIVE = 'active' as const
export const VOUCHER_STATUS_REDEEMED = 'redeemed' as const
export const VOUCHER_STATUS_EXPIRED = 'expired' as const
export const VOUCHER_STATUS_PENDING = 'pending' as const
export const VOUCHER_STATUS_CANCELED = 'canceled' as const
export const VOUCHER_STATUS_UNKNOWN = 'unknown' as const

export type PublicVoucherStatus =
  | typeof VOUCHER_STATUS_ACTIVE
  | typeof VOUCHER_STATUS_REDEEMED
  | typeof VOUCHER_STATUS_EXPIRED
  | typeof VOUCHER_STATUS_PENDING
  | typeof VOUCHER_STATUS_CANCELED
  | typeof VOUCHER_STATUS_UNKNOWN

/**
 * Raw voucher status as emitted by the admin VoucherResource —
 * mirrors backend `App\Enums\VoucherStatus` exactly (4 cases). No
 * `expired` (derived from `expires_at`), no `unknown` (defensive value
 * only present in the public-API resource).
 */
export const VOUCHER_STATUS_VALUES = [
  VOUCHER_STATUS_PENDING,
  VOUCHER_STATUS_ACTIVE,
  VOUCHER_STATUS_REDEEMED,
  VOUCHER_STATUS_CANCELED,
] as const

export type VoucherStatus = (typeof VOUCHER_STATUS_VALUES)[number]

/**
 * PII-free per-voucher payload for the redeem landing page.
 * Intentionally excludes customer info, seller_id, and any internal identifiers.
 */
export interface PublicVoucherSummary {
  code: string
  experienceUuid: string | null
  experienceName: string | null
  experienceImage: string | null
  optionName: string | null
  expiresAt: string | null // ISO 8601
  redeemedAt: string | null // ISO 8601
  status: PublicVoucherStatus
}

/**
 * Group response shape for GET /api/vouchers/group/{groupId}.
 * `defaultLocale` is guaranteed to be a member of `supportedLocales`.
 */
export interface PublicVoucherGroup {
  voucherGroupId: string
  vouchers: PublicVoucherSummary[]
  supportedLocales: string[]
  defaultLocale: string
}

/**
 * Original order returned by POST /api/vouchers/{code}/verify.
 * Aliases the existing project Order type — re-exported here as a named alias
 * for the redemption flow consumer (Plan 01-07+) so callers can import voucher-
 * specific types from a single module without leaking the broader Order import.
 */
export type VoucherOriginalOrder = Order

/**
 * Full response shape for POST /api/vouchers/{code}/verify (W-08).
 *
 * The backend wraps the order under `data` (Laravel JsonResource default) and
 * attaches sibling fields via `additional()` so the redeem voucher page can
 * render a chain-scoped locale switcher even when the user lands on the
 * page directly (without first visiting `/redeem/[groupId]`).
 *
 * Mirrors PublicVoucherController::verify backend response.
 */
export interface PublicVoucherVerifyResult {
  order: VoucherOriginalOrder
  supportedLocales: string[]
  defaultLocale: string
}

/* -------------------------------------------------------------------------- */
/* Voucher source constants                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Named voucher source values (mirror of PHP App\Enums\VoucherSource).
 * Use these constants instead of inline literals like `source === 'external_api'`
 * (W-11 / no magic strings).
 */
export const VOUCHER_SOURCE_INTERNAL = 'internal' as const
export const VOUCHER_SOURCE_EXTERNAL_API = 'external_api' as const

/**
 * Order.source for orders originated by the external API (BOL voucher
 * purchase orders). Mirrors PHP `App\Enums\OrderSource::EXTERNAL_API`.
 * Same string as VOUCHER_SOURCE_EXTERNAL_API by coincidence — different
 * domain (Order vs Voucher) — kept as a separate constant so the check
 * site reads as "this is an externally-sourced order".
 */
export const ORDER_SOURCE_EXTERNAL_API = 'external_api' as const

/**
 * UI sentinel for the admin Source filter "no filter" option (mirror of PHP
 * VoucherDefaults::FILTER_SOURCE_ALL). NOT a backend-emitted value — present
 * only on the FE filter dropdown / URL query param (W-10 / no magic strings).
 */
export const VOUCHER_SOURCE_ALL = 'all' as const

/**
 * All voucher source values as exposed by the backend.
 * `as const` so each value keeps its narrow string-literal type.
 */
export const VOUCHER_SOURCE_VALUES = [VOUCHER_SOURCE_INTERNAL, VOUCHER_SOURCE_EXTERNAL_API] as const

export type VoucherSource = (typeof VOUCHER_SOURCE_VALUES)[number]

/**
 * Filter values for the admin Source filter UI (Plan 01-08).
 * Includes the `VOUCHER_SOURCE_ALL` sentinel for the "no filter" option.
 */
export const VOUCHER_SOURCE_FILTER_VALUES = [
  VOUCHER_SOURCE_ALL,
  ...VOUCHER_SOURCE_VALUES,
] as const

export type VoucherSourceFilter = (typeof VOUCHER_SOURCE_FILTER_VALUES)[number]
